/**
 * Tab Suspender — frees memory by parking idle tabs
 *
 * Declares a single web-accessible-resource entry matched to every origin with no
 * `use_dynamic_url`, whose broad patterns (`*.html`, `*.js`, `img/*.ico`, `/img/*.png`)
 * expose the whole bundle at fixed URLs. Every one of its many icons, popup pages and
 * the `inject.js` content script is fetchable from any page.
 *
 * The `img/new_page.ico` favicon is the primary signal — an image, so it loads through
 * `probeImage` with no CORS grant — corroborated by the `inject.js` content script and a
 * popup page. All three resolve only when the extension is installed.
 *
 * Manifest verified against version 2.0.12.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'fiabciakcmgepblmdkmemdbbkilneeeh';

export const tabSuspender: ExtensionDefinition = {
  id: ID,
  name: 'Tab Suspender',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  notes:
    'One WAR entry on every origin with broad *.html / *.js / img/* patterns and no ' +
    'dynamic URL, so the whole bundle is fetchable. Detection is conclusive.',
  signals: [
    {
      id: 'war-new-page-ico',
      kind: 'web-accessible-resource',
      description:
        'Load img/new_page.ico via <img> — exposed to every origin and readable without ' +
        'a CORS grant, so it survives a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'img/new_page.ico'),
    },
    {
      id: 'war-inject-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch inject.js — the content script exposed to every origin, an independent path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'inject.js'),
    },
    {
      id: 'war-popup-html',
      kind: 'web-accessible-resource',
      description: 'Fetch popup.html — the popup UI page, a third corroborating path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'popup.html'),
    },
  ],
};
